import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ShapeAPI,
  NavigationAPI,
  FloorAPI,
  DestinationAPI,
  resolveAsset,
} from "../../services/api";
import { parseNames } from "../../utils/nameParser";
import {
  MapContainer,
  ImageOverlay,
  Rectangle,
  Circle,
  Polygon,
  useMap,
  useMapEvents,
  CircleMarker,
} from "react-leaflet";
import L from "leaflet";
import { ReactComponent as ClickIcon } from "../../assets/icons/adddestination/click.svg";
import { ReactComponent as HandIcon } from "../../assets/icons/adddestination/hand.svg";
import { ReactComponent as EyeIcon } from "../../assets/icons/adddestination/eye.svg";

const DEFAULT_BG =
  "https://media.wired.com/photos/59269cd37034dc5f91bec0f1/191:100/w_1280,c_limit/GoogleMapTA.jpg";

function getFloorLabel(f, t) {
  const name = f.name || "";
  const level = f.level;
  if (!name) {
    return t("admin.navigation.floorLevel", { defaultValue: `ชั้น ${level}`, level });
  }
  const lowerName = name.toLowerCase().trim();
  const isGeneric =
    lowerName === `floor${level}` ||
    lowerName === `floor ${level}` ||
    lowerName === `floor_${level}` ||
    lowerName === `ชั้น${level}` ||
    lowerName === `ชั้น ${level}` ||
    lowerName === `ชั้นที่${level}` ||
    lowerName === `ชั้นที่ ${level}`;
  if (isGeneric) {
    return t("admin.navigation.floorLevel", { defaultValue: `ชั้น ${level}`, level });
  }
  return name;
}

// ===================
// Coord helpers
// ===================
// pixel (image, top-left origin) → leaflet latLng [lat, lng]
const px2ll = (x, y, imgH) => [imgH - y, x];
// leaflet latLng → pixel
const ll2px = (latlng, imgH) => ({ x: latlng.lng, y: imgH - latlng.lat });

// Detect: shape coords normalized 0-1 (legacy) vs pixel
const looksNormalized = (s) => {
  const vals = [];
  ["x", "y", "width", "height"].forEach((k) => {
    if (typeof s[k] === "number") vals.push(s[k]);
  });
  if (Array.isArray(s.points)) s.points.forEach((p) => vals.push(p.x, p.y));
  if (vals.length === 0) return false;
  return vals.every((v) => v >= 0 && v <= 1);
};

const denormShape = (s, imgW, imgH) => {
  if (!looksNormalized(s)) return s;
  const out = { ...s };
  if (typeof s.x === "number") out.x = s.x * imgW;
  if (typeof s.y === "number") out.y = s.y * imgH;
  if (typeof s.width === "number") out.width = s.width * imgW;
  if (typeof s.height === "number") out.height = s.height * imgH;
  if (Array.isArray(s.points)) {
    out.points = s.points.map((p) => ({ x: p.x * imgW, y: p.y * imgH }));
  }
  return out;
};

const toNumber = (value, fallback = 0) => {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
};

const normalizeShapeForSave = (shape, floorId) => {
  const next = {
    ...shape,
    floorId: shape.floorId ?? shape.floor_id ?? floorId,
  };

  if (Array.isArray(shape.points)) {
    next.points = shape.points.map((p) => ({
      x: toNumber(p.x),
      y: toNumber(p.y),
    }));
  }

  if (next.type === "rectangle" && Array.isArray(next.points) && next.points.length >= 4) {
    const xs = next.points.map((p) => p.x);
    const ys = next.points.map((p) => p.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const maxX = Math.max(...xs);
    const maxY = Math.max(...ys);

    next.x = minX;
    next.y = minY;
    next.width = maxX - minX;
    next.height = maxY - minY;
    next.points = [
      { x: minX, y: minY },
      { x: maxX, y: minY },
      { x: maxX, y: maxY },
      { x: minX, y: maxY },
    ];
  }

  if (next.type !== "rectangle") {
    if (typeof next.x === "number" || typeof shape.x === "string") next.x = toNumber(shape.x);
    if (typeof next.y === "number" || typeof shape.y === "string") next.y = toNumber(shape.y);
    if (typeof next.width === "number" || typeof shape.width === "string") next.width = toNumber(shape.width);
    if (typeof next.height === "number" || typeof shape.height === "string") next.height = toNumber(shape.height);
  }

  return next;
};

const shapeBelongsToFloor = (shape, floorId) => {
  const shapeFloorId = shape.floorId ?? shape.floor_id ?? shape.floor?.id;
  return shapeFloorId == null || String(shapeFloorId) === String(floorId);
};

const getShapeCenter = (shape) => {
  if (Array.isArray(shape.points) && shape.points.length > 0) {
    const sumX = shape.points.reduce((sum, p) => sum + (p.x || 0), 0);
    const sumY = shape.points.reduce((sum, p) => sum + (p.y || 0), 0);
    return {
      x: sumX / shape.points.length,
      y: sumY / shape.points.length,
    };
  }
  const x = shape.x || 0;
  const y = shape.y || 0;
  const w = shape.width || 0;
  const h = shape.height || 0;
  return {
    x: x + w / 2,
    y: y + h / 2,
  };
};

const mapShapeCategoryToDest = (shapeCategory) => {
  if (shapeCategory === "toilet" || shapeCategory === "bathroom") return "bathroom";
  if (shapeCategory === "stair" || shapeCategory === "stairs") return "stairs";
  if (shapeCategory === "elevator") return "elevator";
  if (shapeCategory === "bridge") return "bridge";
  return "counterservice"; // default/fallback
};

// ===================
// FitBounds: fit ตาม bounds ใหม่เมื่อรูปเปลี่ยน
// ===================
function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (!bounds) return;
    // invalidate ก่อน เพื่อให้ leaflet วัด container ใหม่ (กันกรณี mount ตอน parent ยัง layout ไม่เสร็จ)
    map.invalidateSize();
    map.fitBounds(bounds, { padding: [20, 20], animate: false });
    // เผื่อ layout settle ช้า re-fit อีกที
    const t = setTimeout(() => {
      map.invalidateSize();
      map.fitBounds(bounds, { padding: [20, 20], animate: false });
    }, 120);
    return () => clearTimeout(t);
  }, [bounds, map]);
  return null;
}

// ===================
// Drawing Layer: จัดการการลากวาด shape ใหม่
// ===================
function DrawingLayer({
  activeSubtool,
  defaultColor,
  imgH,
  onCreate,
  enabled,
}) {
  const [start, setStart] = useState(null);
  const [draft, setDraft] = useState(null);
  const map = useMap();

  useEffect(() => {
    if (enabled) {
      map.dragging.disable();
      map.getContainer().style.cursor = "crosshair";
    } else {
      map.dragging.enable();
      map.getContainer().style.cursor = "";
    }
    return () => {
      map.dragging.enable();
      if (map.getContainer()) map.getContainer().style.cursor = "";
    };
  }, [enabled, map]);

  useMapEvents({
    mousedown: (e) => {
      if (!enabled) return;
      const { x, y } = ll2px(e.latlng, imgH);
      setStart({ x, y });
      setDraft({
        type: activeSubtool,
        x,
        y,
        width: 0,
        height: 0,
        color: defaultColor,
      });
    },
    mousemove: (e) => {
      if (!start) return;
      const { x: cx, y: cy } = ll2px(e.latlng, imgH);
      setDraft({
        type: activeSubtool,
        x: Math.min(start.x, cx),
        y: Math.min(start.y, cy),
        width: Math.abs(cx - start.x),
        height: Math.abs(cy - start.y),
        color: defaultColor,
      });
    },
    mouseup: () => {
      if (!start || !draft) {
        setStart(null);
        setDraft(null);
        return;
      }
      if (draft.width > 3 && draft.height > 3) {
        let shape;
        if (draft.type === "rectangle") {
          shape = {
            id: Date.now(),
            type: "rectangle",
            color: draft.color,
            name: "",
            points: [
              { x: draft.x, y: draft.y },
              { x: draft.x + draft.width, y: draft.y },
              { x: draft.x + draft.width, y: draft.y + draft.height },
              { x: draft.x, y: draft.y + draft.height },
            ],
          };
        } else {
          shape = {
            id: Date.now(),
            type: draft.type,
            color: draft.color,
            name: "",
            x: draft.x,
            y: draft.y,
            width: draft.width,
            height: draft.height,
          };
        }
        onCreate(shape);
      }
      setStart(null);
      setDraft(null);
    },
  });

  if (!draft || !enabled || activeSubtool === "polygon") return null;
  const color = draft.color || "#666";
  const opts = {
    color,
    fillColor: color,
    fillOpacity: 0.25,
    weight: 2,
    dashArray: "6 4",
    interactive: false,
  };
  if (draft.type === "rectangle") {
    return (
      <Rectangle
        bounds={[
          px2ll(draft.x, draft.y + draft.height, imgH),
          px2ll(draft.x + draft.width, draft.y, imgH),
        ]}
        pathOptions={opts}
      />
    );
  }
  if (draft.type === "circle") {
    const cx = draft.x + draft.width / 2;
    const cy = draft.y + draft.height / 2;
    const r = Math.max(draft.width, draft.height) / 2;
    return <Circle center={px2ll(cx, cy, imgH)} radius={r} pathOptions={opts} />;
  }
  if (draft.type === "triangle") {
    const pts = [
      px2ll(draft.x + draft.width / 2, draft.y, imgH),
      px2ll(draft.x, draft.y + draft.height, imgH),
      px2ll(draft.x + draft.width, draft.y + draft.height, imgH),
    ];
    return <Polygon positions={pts} pathOptions={opts} />;
  }
  return null;
}

// ===================
// Polygon Drawing Layer: คลิกวางจุด, ดับเบิลคลิกปิดรูป
// ===================
function PolygonDrawingLayer({ defaultColor, imgH, onCreate, enabled }) {
  const [points, setPoints] = useState([]);
  const [cursorPt, setCursorPt] = useState(null);
  const map = useMap();

  // useRef เพื่อให้ closures อ่านค่า points ล่าสุดได้เสมอ (ไม่ stale)
  const pointsRef = useRef([]);
  // ป้องกัน finalize ถูกเรียก 2 ครั้ง (dblclick = click + click + dblclick)
  const finalizedRef = useRef(false);

  const syncPoints = (next) => {
    pointsRef.current = next;
    setPoints(next);
  };

  useEffect(() => {
    if (enabled) {
      map.dragging.disable();
      map.getContainer().style.cursor = "crosshair";
    } else {
      map.dragging.enable();
      map.getContainer().style.cursor = "";
      syncPoints([]);
      setCursorPt(null);
      finalizedRef.current = false;
    }
    return () => {
      map.dragging.enable();
      if (map.getContainer()) map.getContainer().style.cursor = "";
    };
  }, [enabled, map]);

  // ESC ยกเลิก
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e) => {
      if (e.key === "Escape") { syncPoints([]); setCursorPt(null); finalizedRef.current = false; }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled]);

  const SNAP_DIST = 12;

  const finalize = () => {
    // ป้องกัน double finalize (เกิดจาก dblclick ที่ยิง click 2 ครั้งก่อน)
    if (finalizedRef.current) return;
    finalizedRef.current = true;

    const current = pointsRef.current;
    if (current.length >= 3) {
      onCreate({
        id: Date.now(),
        type: "polygon",
        color: defaultColor,
        name: "",
        points: current,
      });
    }
    syncPoints([]);
    setCursorPt(null);

    // reset flag หลัง event loop ปัจจุบันสิ้นสุด
    setTimeout(() => { finalizedRef.current = false; }, 300);
  };

  useMapEvents({
    mousemove: (e) => {
      if (!enabled) return;
      const { x, y } = ll2px(e.latlng, imgH);
      setCursorPt({ x, y });
    },
    click: (e) => {
      if (!enabled || finalizedRef.current) return;
      L.DomEvent.stopPropagation(e);
      const { x, y } = ll2px(e.latlng, imgH);

      // ถ้ามีจุดแล้ว >= 3 และคลิกใกล้จุดแรก → ปิดรูป
      const current = pointsRef.current;
      if (current.length >= 3) {
        const first = current[0];
        const containerFirst = map.latLngToContainerPoint(px2ll(first.x, first.y, imgH));
        const containerCur = map.latLngToContainerPoint(e.latlng);
        if (containerFirst.distanceTo(containerCur) < SNAP_DIST) {
          finalize();
          return;
        }
      }
      syncPoints([...current, { x, y }]);
    },
    dblclick: (e) => {
      if (!enabled) return;
      L.DomEvent.stopPropagation(e);
      finalize();
    },
  });

  if (!enabled) return null;

  const color = defaultColor || "#94a3b8";
  const opts = { color, fillColor: color, fillOpacity: 0.25, weight: 2, dashArray: "6 4", interactive: false };
  const handleOpts = { color: "#1e40af", fillColor: "#ffffff", fillOpacity: 1, weight: 2, interactive: false };

  const previewPts = cursorPt && points.length > 0
    ? [...points, cursorPt].map((p) => px2ll(p.x, p.y, imgH))
    : points.map((p) => px2ll(p.x, p.y, imgH));

  const isNearFirst = () => {
    if (!cursorPt || points.length < 3) return false;
    const first = points[0];
    const containerFirst = map.latLngToContainerPoint(px2ll(first.x, first.y, imgH));
    const containerCur = map.latLngToContainerPoint(px2ll(cursorPt.x, cursorPt.y, imgH));
    return containerFirst.distanceTo(containerCur) < SNAP_DIST;
  };

  return (
    <>
      {previewPts.length >= 2 && (
        <Polygon
          positions={previewPts}
          pathOptions={{
            ...opts,
            dashArray: isNearFirst() ? null : "6 4",
            fillOpacity: isNearFirst() ? 0.35 : 0.15,
          }}
        />
      )}

      {points.map((p, i) => (
        <CircleMarker
          key={i}
          center={px2ll(p.x, p.y, imgH)}
          radius={i === 0 && points.length >= 3 ? 9 : 5}
          pathOptions={i === 0 && points.length >= 3
            ? { ...handleOpts, color: "#16a34a", fillColor: "#bbf7d0" }
            : handleOpts
          }
        />
      ))}
    </>
  );
}

// ===================
// Render shape (committed) on map
// ===================
function ShapeLayer({ shape, imgH, isSelected, onSelect }) {
  const color = shape.color || "#9ca3af";
  const isHidden = !!shape.hidden;
  const opts = {
    color,
    fillColor: color,
    fillOpacity: isHidden ? 0.08 : 0.5,
    opacity: isHidden ? 0.5 : 1,
    weight: isSelected ? 3 : isHidden ? 1 : 1.5,
    dashArray: isSelected ? "8 4" : isHidden ? "4 4" : null,
  };
  const handlers = {
    click: (e) => {
      L.DomEvent.stopPropagation(e);
      onSelect(shape.id);
    },
  };
  if (shape.type === "rectangle" && Array.isArray(shape.points)) {
    return (
      <Polygon
        positions={shape.points.map((p) => px2ll(p.x, p.y, imgH))}
        pathOptions={opts}
        eventHandlers={handlers}
      />
    );
  }
  if (shape.type === "circle") {
    const cx = (shape.x || 0) + (shape.width || 0) / 2;
    const cy = (shape.y || 0) + (shape.height || 0) / 2;
    const r = Math.max(shape.width || 0, shape.height || 0) / 2;
    return (
      <Circle
        center={px2ll(cx, cy, imgH)}
        radius={r}
        pathOptions={opts}
        eventHandlers={handlers}
      />
    );
  }
  if (shape.type === "polygon") {
    if (!Array.isArray(shape.points) || shape.points.length < 3) return null;
    return (
      <Polygon
        positions={shape.points.map((p) => px2ll(p.x, p.y, imgH))}
        pathOptions={opts}
        eventHandlers={handlers}
      />
    );
  }
  if (shape.type === "triangle") {
    // ถ้ามี custom points ให้ใช้ points (หลังลาก corner)
    const pts = Array.isArray(shape.points) && shape.points.length === 3
      ? shape.points.map((p) => px2ll(p.x, p.y, imgH))
      : (() => {
        const x = shape.x || 0;
        const y = shape.y || 0;
        const w = shape.width || 0;
        const h = shape.height || 0;
        return [
          px2ll(x + w / 2, y, imgH),
          px2ll(x, y + h, imgH),
          px2ll(x + w, y + h, imgH),
        ];
      })();
    return (
      <Polygon
        positions={pts}
        pathOptions={opts}
        eventHandlers={handlers}
      />
    );
  }
  return null;
}

// ===================
// Click on empty map → deselect
// ===================
function MapClickHandler({ onClick }) {
  useMapEvents({
    click: () => onClick(),
  });
  return null;
}

// ===================
// Corner Handles: ลาก vertex แต่ละจุดของ shape ที่เลือก
// ===================
function CornerHandles({ shape, imgH, onUpdate }) {
  const map = useMap();
  const draggingIdx = useRef(null);

  // คำนวณ pixel vertices จาก shape
  const getVertices = (s) => {
    if ((s.type === "rectangle" || s.type === "polygon") && Array.isArray(s.points)) {
      return s.points.map((p) => ({ x: p.x, y: p.y }));
    }
    if (s.type === "triangle") {
      // ถ้าเคยลาก corner มาแล้วจะมี points
      if (Array.isArray(s.points) && s.points.length === 3) {
        return s.points.map((p) => ({ x: p.x, y: p.y }));
      }
      const x = s.x || 0, y = s.y || 0, w = s.width || 0, h = s.height || 0;
      return [
        { x: x + w / 2, y: y },
        { x: x, y: y + h },
        { x: x + w, y: y + h },
      ];
    }
    return [];
  };

  const verticesRef = useRef([]);
  verticesRef.current = getVertices(shape);

  const applyUpdate = (idx, newX, newY) => {
    const newVertices = verticesRef.current.map((v, i) =>
      i === idx ? { x: newX, y: newY } : v
    );
    if (shape.type === "rectangle" || shape.type === "polygon") {
      onUpdate(shape.id, { points: newVertices });
    } else if (shape.type === "triangle") {
      const minX = Math.min(...newVertices.map((v) => v.x));
      const minY = Math.min(...newVertices.map((v) => v.y));
      const maxX = Math.max(...newVertices.map((v) => v.x));
      const maxY = Math.max(...newVertices.map((v) => v.y));
      onUpdate(shape.id, {
        x: minX, y: minY,
        width: maxX - minX, height: maxY - minY,
        points: newVertices,
      });
    }
  };

  // ฟัง global map events เพื่อ track drag อย่างต่อเนื่อง
  useMapEvents({
    mousemove: (e) => {
      if (draggingIdx.current === null) return;
      const { x, y } = ll2px(e.latlng, imgH);
      applyUpdate(draggingIdx.current, x, y);
    },
    mouseup: () => {
      if (draggingIdx.current !== null) {
        draggingIdx.current = null;
        map.dragging.enable();
      }
    },
  });

  const vertices = verticesRef.current;
  if (vertices.length === 0) return null;

  return (
    <>
      {vertices.map((v, idx) => (
        <CircleMarker
          key={idx}
          center={px2ll(v.x, v.y, imgH)}
          radius={7}
          pathOptions={{
            color: "#1e40af",
            fillColor: "#ffffff",
            fillOpacity: 1,
            weight: 2.5,
          }}
          eventHandlers={{
            mousedown: (e) => {
              L.DomEvent.stopPropagation(e);
              draggingIdx.current = idx;
              map.dragging.disable();
            },
          }}
        />
      ))}
    </>
  );
}

// ===================
// MAIN
// ===================
export default function MapDrawing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const floorId = searchParams.get("floorId");
  const buildingId = searchParams.get("buildingId");

  const [floorOptions, setFloorOptions] = useState([]);
  const [floorData, setFloorData] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [localPreview, setLocalPreview] = useState(null);

  const [shapes, setShapes] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [selectedShapeId, setSelectedShapeId] = useState(null);
  const [names, setNames] = useState([""]);
  const [activeTool, setActiveTool] = useState("mouse");
  const [activeSubtool, setActiveSubtool] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const defaultShapeColor = "#fbcfe8";
  const [openModel, setOpenModel] = useState(false);
  const [shapeImages, setShapeImages] = useState({});
  const [saving, setSaving] = useState(false);
  // popup after drawing: set name + color before committing shape
  const [pendingShape, setPendingShape] = useState(null);
  const [pendingName, setPendingName] = useState("");
  const [pendingColor, setPendingColor] = useState("#fbcfe8");
  // zoom control
  const mapRef = useRef(null);
  const [zoomLevel, setZoomLevel] = useState(0);

  // image natural size (fallback ถ้า backend ยังไม่มี)
  const [detectedSize, setDetectedSize] = useState({ w: 0, h: 0 });

  // โหลด floors
  useEffect(() => {
    if (!buildingId) return;
    FloorAPI.listByBuilding(buildingId)
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.items || [];
        setFloorOptions(list);
        if (!floorId && list.length > 0) {
          setSearchParams(
            { buildingId, floorId: String(list[0].id) },
            { replace: true }
          );
        }
      })
      .catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId]);

  // โหลด floor data
  useEffect(() => {
    if (!floorId) {
      setFloorData(null);
      return;
    }
    NavigationAPI.getFloor(floorId)
      .then((d) => setFloorData(d))
      .catch(() => setFloorData(null));
  }, [floorId]);

  // โหลดสถานที่เพิ่มเติมทั้งหมดของชั้นนี้เพื่อทำ Dropdown เชื่อมโยง
  useEffect(() => {
    if (!floorId) {
      setDestinations([]);
      return;
    }
    DestinationAPI.listByFloor(floorId)
      .then((d) => setDestinations(Array.isArray(d) ? d : []))
      .catch(() => setDestinations([]));
  }, [floorId]);

  const bgImage =
    localPreview || resolveAsset(floorData?.imageUrl) || DEFAULT_BG;

  // detect natural size จากรูปที่แสดงจริง (เป็นแหล่งความจริงเดียว — กันค่า backend stale ตอนเปลี่ยนรูป)
  useEffect(() => {
    if (!bgImage) return;
    // reset ก่อน เพื่อให้ dimsReady = false ระหว่างกำลังโหลดรูปใหม่
    setDetectedSize({ w: 0, h: 0 });
    const im = new window.Image();
    im.onload = () => {
      if (im.naturalWidth && im.naturalHeight) {
        setDetectedSize({ w: im.naturalWidth, h: im.naturalHeight });
      }
    };
    im.src = bgImage;
  }, [bgImage]);

  // ขนาดรูปจริง: ใช้ค่าที่ตรวจจับจากรูปจริงเป็นหลัก (กันค่า backend stale)
  // fallback ไปดู backend เผื่อตรวจจับไม่ได้
  const realImgW = detectedSize.w || floorData?.imageWidth || 0;
  const realImgH = detectedSize.h || floorData?.imageHeight || 0;
  const dimsReady = realImgW > 0 && realImgH > 0;
  const imgW = realImgW || 1000;
  const imgH = realImgH || 700;
  const bounds = useMemo(
    () => [
      [0, 0],
      [imgH, imgW],
    ],
    [imgW, imgH]
  );

  // โหลด shapes — รอจนกว่าจะรู้ขนาดรูปจริง (กัน shape ถูก scale ผิด)
  useEffect(() => {
    setShapes([]);
    setSelectedShapeId(null);
    if (!floorId) {
      return;
    }
    if (!dimsReady) return;
    ShapeAPI.listByFloor(floorId)
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setShapes(
          list
            .filter((s) => shapeBelongsToFloor(s, floorId))
            .map((s) => denormShape(s, imgW, imgH))
        );
      })
      .catch(() => setShapes([]));
  }, [floorId, dimsReady, imgW, imgH]);



  const handleCreateShape = (shape) => {
    // เปิด popup ให้ตั้งชื่อ+สีก่อน commit
    setPendingShape(shape);
    setPendingName("");
    setPendingColor(defaultShapeColor);
    setActiveSubtool(null);
    setActiveTool("mouse");
  };

  const handleConfirmPending = () => {
    if (!pendingShape) return;
    const committed = { ...pendingShape, name: pendingName, color: pendingColor };
    setShapes((prev) => [...prev, committed]);
    setSelectedShapeId(committed.id);
    setPendingShape(null);
  };

  const handleCancelPending = () => setPendingShape(null);

  const handleSelectShape = (id) => {
    setSelectedShapeId(id);
  };

  const handleUpdateShape = (id, patch) => {
    setShapes((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...patch } : s))
    );
  };

  const handleRemoveShape = async (id) => {
    const shapeObj = shapes.find((s) => s.id === id);
    if (shapeObj && shapeObj.destinationId) {
      if (window.confirm("คุณต้องการลบสถานที่เชื่อมโยงนี้ด้วยหรือไม่?")) {
        try {
          await DestinationAPI.remove(shapeObj.destinationId);
          // Reload destinations
          const updatedDests = await DestinationAPI.listByFloor(floorId);
          setDestinations(Array.isArray(updatedDests) ? updatedDests : []);
        } catch (err) {
          console.warn("Failed to delete linked destination:", err);
        }
      }
    }
    setShapes((prev) => prev.filter((s) => s.id !== id));
    if (selectedShapeId === id) setSelectedShapeId(null);
  };

  const handleSave = async () => {
    if (!floorId) return;
    setSaving(true);
    try {
      const nextShapes = [...shapes];

      for (let i = 0; i < nextShapes.length; i++) {
        const shape = nextShapes[i];

        // Skip corridors or shapes without names
        if (shape.category === "corridor" || !shape.name) {
          continue;
        }

        const center = getShapeCenter(shape);
        const payload = {
          name: shape.name,
          category: mapShapeCategoryToDest(shape.category),
          x: Math.round(center.x),
          y: Math.round(center.y),
        };

        const imageFile = shapeImages[shape.id];
        if (imageFile) {
          payload.image = imageFile;
        }

        // If shape already has destinationId, update it
        if (shape.destinationId) {
          try {
            await DestinationAPI.update(shape.destinationId, payload);
          } catch (err) {
            console.error(`Failed to update destination ${shape.destinationId}:`, err);
            if (err.status === 404) {
              const newDest = await DestinationAPI.create(floorId, payload);
              shape.destinationId = newDest.id;
            } else {
              throw err;
            }
          }
        } else {
          // If no destinationId, check if a destination with the same name already exists on this floor
          const existing = destinations.find((d) => d.name === shape.name);
          if (existing) {
            await DestinationAPI.update(existing.id, payload);
            shape.destinationId = existing.id;
          } else {
            // Create new destination
            const newDest = await DestinationAPI.create(floorId, payload);
            shape.destinationId = newDest.id;
          }
        }
      }

      // Save shapes
      const payload = nextShapes.map((shape) => normalizeShapeForSave(shape, floorId));
      await ShapeAPI.bulkSave(floorId, payload);
      setShapes(payload);
      setShapeImages({});

      // Reload destinations from server
      const updatedDests = await DestinationAPI.listByFloor(floorId);
      setDestinations(Array.isArray(updatedDests) ? updatedDests : []);

      setOpenModel(true);
    } catch (e) {
      alert(e.message || t("admin.mapDrawing.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const selectedShape = shapes.find((s) => s.id === selectedShapeId);
  const selectedShapeLinkedDest = selectedShape
    ? (selectedShape.destinationId
      ? destinations.find((d) => String(d.id) === String(selectedShape.destinationId))
      : destinations.find((d) => d.name === selectedShape.name))
    : null;
  const selectedShapeImageUrl = selectedShapeLinkedDest
    ? (selectedShapeLinkedDest.image || selectedShapeLinkedDest.imageUrl || selectedShapeLinkedDest.img)
    : (selectedShape ? (selectedShape.imageUrl || selectedShape.image || selectedShape.img) : "");

  useEffect(() => {
    if (!selectedShape) {
      setNames([""]);
      return;
    }
    const rawName = selectedShape.name || "";
    const parsed = parseNames(rawName);
    const hasDiff = parsed.all.length !== names.length || parsed.all.some((n, idx) => n !== names[idx]);
    if (hasDiff) {
      setNames(parsed.all.length > 0 ? parsed.all : [""]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShapeId, selectedShape?.name]);

  const handleNameChange = (index, value) => {
    const newNames = [...names];
    newNames[index] = value;
    setNames(newNames);

    const combinedName = newNames.map(n => n.trim()).filter(Boolean).join(", ");
    handleUpdateShape(selectedShape.id, { name: combinedName });
  };

  const handleAddNameField = () => {
    setNames([...names, ""]);
  };

  const handleRemoveNameField = (index) => {
    const newNames = names.filter((_, i) => i !== index);
    setNames(newNames);
    const combinedName = newNames.map(n => n.trim()).filter(Boolean).join(", ");
    handleUpdateShape(selectedShape.id, { name: combinedName });
  };

  const drawOptions = [
    { id: "rectangle", label: "▢", name: t("admin.mapDrawing.shapeRectangle") },
    { id: "circle", label: "○", name: t("admin.mapDrawing.shapeCircle") },
    { id: "triangle", label: "△", name: t("admin.mapDrawing.shapeTriangle") },
    { id: "polygon", label: "⬡", name: t("admin.mapDrawing.shapeFreePolygon") },
  ];

  const colorOptions = [
    "#f87170",//red
    "#fbcfe8", // pink
    "#fed7aa", // orange
    "#bbf7d0", // green
    "#bae6fd", // blue
    "#e5e7eb", // gray
    "#ffffff", // white
  ];

  const drawingEnabled = activeTool === "draw" && !!activeSubtool;

  // แปลง Leaflet zoom level → % (zoom 0 = 100%, เพิ่ม/ลดทีละ 25%)
  const zoomPercent = Math.round(Math.pow(2, zoomLevel) * 100);

  const handleZoomIn = () => mapRef.current?.zoomIn();
  const handleZoomOut = () => mapRef.current?.zoomOut();
  const [showBgImage, setShowBgImage] = useState(true);

  // Component ใน MapContainer เพื่อ expose map instance + track zoom
  function MapController() {
    const map = useMap();
    useEffect(() => {
      mapRef.current = map;
      setZoomLevel(map.getZoom());
      const onZoom = () => setZoomLevel(map.getZoom());
      map.on("zoom", onZoom);
      return () => map.off("zoom", onZoom);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map]);
    return null;
  }

  const getShapeLabel = (s, idx) => {
    if (s.name) return parseNames(s.name).primary;
    const typeName =
      s.type === "rectangle"
        ? t("admin.mapDrawing.shapeRectangle")
        : s.type === "circle"
          ? t("admin.mapDrawing.shapeCircle")
          : s.type === "triangle"
            ? t("admin.mapDrawing.shapeTriangle")
            : t("admin.mapDrawing.shapeLabel");
    return `${typeName} ${idx + 1}`;
  };

  return (
    <div className="font-sans w-full h-full">
      <div className="flex w-full h-full">
        {/* LEFT: map area */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* top bar */}
          <div className="flex items-center justify-between bg-white px-3 shrink-0 border-b border-gray-200" style={{ height: '72px' }}>

            {/* floor tabs */}
            <div className="flex items-center gap-1 overflow-x-auto mr-8">
              {floorOptions.map((f) => (
                <button
                  key={f.id}
                  onClick={() =>
                    setSearchParams(
                      { ...(buildingId ? { buildingId } : {}), floorId: String(f.id) },
                      { replace: true }
                    )
                  }
                  className={`px-5 h-10 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${String(f.id) === String(floorId)
                    ? "bg-blue-500 text-white"
                    : "bg-white text-slate-500 hover:bg-gray-100"
                    }`}
                >
                  {getFloorLabel(f, t)}
                </button>
              ))}
            </div>

            {/* tools — right */}
            <div className="flex items-center gap-0.5">

              {/* mouse */}
              <button
                title={t("admin.mapDrawing.toolSelectEdit")}
                onClick={() => { setActiveTool("mouse"); setOpenMenu(null); setActiveSubtool(null); }}
                className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${activeTool === "mouse" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-gray-100"
                  }`}
              >
                <ClickIcon className="w-4 h-4" />
              </button>

              {/* hand */}
              <button
                title={t("admin.mapDrawing.toolPanDrag")}
                onClick={() => { setActiveTool("hand"); setOpenMenu(null); setActiveSubtool(null); }}
                className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${activeTool === "hand" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-gray-100"
                  }`}
              >
                <HandIcon className="w-4 h-4" />
              </button>

              {/* eye */}
              <button
                title={t("admin.mapDrawing.toolView")}
                onClick={() => { setActiveTool("eye"); setOpenMenu(null); setActiveSubtool(null); }}
                className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${activeTool === "eye" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-gray-100"
                  }`}
              >
                <EyeIcon className="w-4 h-4" />
              </button>

              {/* draw dropdown */}
              <div className="relative">
                <button
                  title={t("admin.mapDrawing.toolDrawShape")}
                  onClick={() => {
                    setActiveTool("draw");
                    setOpenMenu(openMenu === "draw" ? null : "draw");
                  }}
                  className={`h-10 px-3 flex items-center gap-1 rounded-lg transition-colors border ${activeTool === "draw"
                    ? "bg-blue-600 text-white border-blue-600"
                    : "text-slate-600 hover:bg-gray-100 border-slate-300"
                    }`}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" strokeDasharray="4 2" />
                  </svg>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </button>

                {openMenu === "draw" && (
                  <div className="absolute right-0 top-full mt-1 w-48 rounded-lg border border-slate-200 bg-white shadow-xl z-[1000]">
                    {drawOptions.map((opt) => (
                      <button
                        type="button"
                        key={opt.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveTool("draw");
                          setActiveSubtool(opt.id);
                          setOpenMenu(null);
                        }}
                        className={`flex w-full items-center gap-2 px-3 py-2 text-sm text-left ${activeSubtool === opt.id ? "bg-blue-50 text-blue-600" : "text-slate-600 hover:bg-slate-100"
                          }`}
                      >
                        <span className="w-5 text-center">{opt.label}</span>
                        <span>{opt.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* zoom + */}
              <button
                onClick={handleZoomIn}
                className="w-10 h-10 flex items-center justify-center rounded-lg text-slate-600 hover:bg-gray-100"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </button>

              {/* zoom % */}
              <span className="text-sm text-slate-500 select-none min-w-[44px] text-center">
                {zoomPercent}%
              </span>

              {/* zoom − */}
              <button
                onClick={handleZoomOut}
                className="w-10 h-10 flex items-center justify-center rounded-lg text-slate-600 hover:bg-gray-100"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M5 12h14" />
                </svg>
              </button>

            </div>
          </div>

          {/* drawing hint */}
          {drawingEnabled && (
            <div className="bg-amber-50 border-b border-amber-200 px-3 py-1 text-xs text-amber-800">
              {activeSubtool === "polygon"
                ? t("admin.mapDrawing.drawingFreePolygonHelp")
                : t("admin.mapDrawing.drawingStandardShapeHelp", { shape: drawOptions.find((o) => o.id === activeSubtool)?.name })
              }
            </div>
          )}

          {/* MAP */}
          <div className="relative flex-1 min-h-[500px] bg-slate-200">
            {!dimsReady && (
              <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-sm z-10 bg-slate-200">
                {t("admin.mapDrawing.loadingMap")}
              </div>
            )}
            {dimsReady && (
              <MapContainer
                key={`${floorId}-${imgW}-${imgH}`}
                crs={L.CRS.Simple}
                bounds={bounds}
                minZoom={-3}
                maxZoom={4}
                zoomSnap={0.25}
                zoomDelta={0.5}
                attributionControl={false}
                zoomControl={false}
                className="w-full h-full"
                style={{ background: "#f1f5f9" }}
              >
                <MapController />
                <FitBounds bounds={bounds} />
                <ImageOverlay url={bgImage} bounds={bounds} opacity={showBgImage ? 1 : 0} />

                {!drawingEnabled && (
                  <MapClickHandler onClick={() => setSelectedShapeId(null)} />
                )}

                {shapes.map((s) => (
                  <React.Fragment key={s.id}>
                    <ShapeLayer
                      shape={s}
                      imgH={imgH}
                      isSelected={selectedShapeId === s.id}
                      onSelect={handleSelectShape}
                    />
                    {selectedShapeId === s.id && !drawingEnabled && (
                      <CornerHandles
                        shape={s}
                        imgH={imgH}
                        onUpdate={handleUpdateShape}
                      />
                    )}
                  </React.Fragment>
                ))}

                <DrawingLayer
                  activeSubtool={activeSubtool}
                  defaultColor={defaultShapeColor}
                  imgH={imgH}
                  onCreate={handleCreateShape}
                  enabled={drawingEnabled && activeSubtool !== "polygon"}
                />
                <PolygonDrawingLayer
                  defaultColor={defaultShapeColor}
                  imgH={imgH}
                  onCreate={handleCreateShape}
                  enabled={drawingEnabled && activeSubtool === "polygon"}
                />
              </MapContainer>
            )}
          </div>
        </div>

        {/* RIGHT: layers panel */}
        <div className="w-[320px] shrink-0 h-full flex flex-col border-l border-gray-200 bg-white">

          {/* header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <span className="text-sm font-semibold transition-colors text-slate-500">Layers</span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">แสดงแผนที่รูปภาพที่อัปโหลด</span>
              {/* toggle ควบคุม ImageOverlay opacity */}
              <button
                onClick={() => setShowBgImage(prev => !prev)}
                className={`relative w-10 h-6 rounded-full transition-colors ${showBgImage ? "bg-green-500" : "bg-slate-300"
                  }`}
              >
                <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${showBgImage ? "left-[18px]" : "left-0.5"
                  }`} />
              </button>
            </div>
          </div>

          {/* selected shape editor — ไม่เปลี่ยนฟังก์ชัน */}
          {selectedShape && (
            <div className="border-b border-gray-200 p-3 bg-blue-50">
              <div className="text-xs font-bold text-blue-800 mb-2">
                {t("admin.mapDrawing.editSelectedShape")}
              </div>
              <label className="block text-[11px] text-slate-600 mb-1">
                {t("admin.mapDrawing.linkDestination") || "เชื่อมโยงสถานที่เพิ่มเติม (จากหน้าบันทึกจุด)"}
              </label>
              <select
                value={selectedShape.destinationId || ""}
                onChange={(e) => {
                  const destId = e.target.value;
                  if (!destId) {
                    handleUpdateShape(selectedShape.id, {
                      destinationId: null,
                    });
                  } else {
                    const dest = destinations.find((d) => String(d.id) === String(destId));
                    if (dest) {
                      handleUpdateShape(selectedShape.id, {
                        destinationId: dest.id,
                        name: dest.name,
                        category: dest.category,
                      });
                    }
                  }
                }}
                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:border-blue-500 focus:outline-none mb-3 bg-white"
              >
                <option value="">{t("admin.mapDrawing.noLink") || "-- ไม่เชื่อมโยง --"}</option>
                {destinations.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} ({d.category})
                  </option>
                ))}
              </select>
              <label className="block text-[11px] text-slate-600 mb-1">
                {t("admin.mapDrawing.roomPlaceName")}
              </label>

              {names.map((name, index) => (
                <div key={index} className="flex items-center gap-2 mb-2">
                  <input
                    type="text"
                    placeholder={
                      index === 0
                        ? t("admin.mapDrawing.roomPlaceNamePlaceholder", "เช่น ห้องตรวจ 1, OPD, ห้องน้ำ ...")
                        : `${t("admin.mapDrawing.roomAliasPlaceholder", "ชื่ออื่น ๆ")} ${index}`
                    }
                    value={name}
                    onChange={(e) => handleNameChange(index, e.target.value)}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:border-blue-500 focus:outline-none"
                  />
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveNameField(index)}
                      className="px-2 py-1 text-red-500 hover:bg-red-50 rounded border border-red-200 text-xs font-semibold shrink-0 transition"
                    >
                      {t("common.delete", "ลบ")}
                    </button>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={handleAddNameField}
                className="mt-1 mb-3 px-3 py-1 border border-dashed border-blue-300 text-blue-600 hover:bg-blue-50 rounded-lg text-[11px] font-bold transition flex items-center gap-1"
              >
                <span>+</span> {t("admin.mapDrawing.addAliasBtn", "เพิ่มชื่ออื่น")}
              </button>
              <label className="block text-[11px] text-slate-600 mt-3 mb-1">
                {t("admin.mapDrawing.imageLabel") || "รูปภาพสถานที่"}
              </label>
              {shapeImages[selectedShape.id] ? (
                <div className="mb-2 relative w-24 h-24 rounded-lg overflow-hidden border border-slate-300 bg-white">
                  <img
                    src={URL.createObjectURL(shapeImages[selectedShape.id])}
                    alt="New Preview"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setShapeImages((prev) => {
                        const copy = { ...prev };
                        delete copy[selectedShape.id];
                        return copy;
                      });
                    }}
                    className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 text-[10px] w-5 h-5 flex items-center justify-center font-bold hover:bg-red-700"
                  >
                    ×
                  </button>
                </div>
              ) : selectedShapeImageUrl ? (
                <div className="mb-2 relative w-24 h-24 rounded-lg overflow-hidden border border-slate-300 bg-white">
                  <img
                    src={resolveAsset(selectedShapeImageUrl)}
                    alt="Current"
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : null}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setShapeImages((prev) => ({
                      ...prev,
                      [selectedShape.id]: file,
                    }));
                  }
                }}
                className="w-full text-xs text-slate-500 file:mr-4 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <label className="block text-[11px] text-slate-600 mt-3 mb-1">
                {t("admin.mapDrawing.category")}
              </label>
              <select
                value={selectedShape.category || ""}
                onChange={(e) => handleUpdateShape(selectedShape.id, { category: e.target.value || undefined })}
                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:border-blue-500 focus:outline-none"
              >
                <option value="">{t("admin.mapDrawing.catUnspecified")}</option>
                <option value="room">{t("admin.mapDrawing.catRoom")}</option>
                <option value="corridor">{t("admin.mapDrawing.catCorridor")}</option>
                <option value="stair">{t("admin.mapDrawing.catStair")}</option>
                <option value="elevator">{t("admin.mapDrawing.catElevator")}</option>
                <option value="toilet">{t("admin.mapDrawing.catToilet")}</option>
                <option value="entrance">{t("admin.mapDrawing.catEntrance")}</option>
                <option value="bridge">{t("admin.mapDrawing.catBridge", "ทางเชื่อม")}</option>
              </select>
              <label className="block text-[11px] text-slate-600 mt-3 mb-1">
                {t("admin.mapDrawing.colorLabel")}
              </label>
              <div className="flex gap-1.5 flex-wrap">
                {colorOptions.map((c) => (
                  <button
                    key={c}
                    onClick={() => handleUpdateShape(selectedShape.id, { color: c })}
                    className="w-7 h-7 rounded border-2"
                    style={{ background: c, borderColor: selectedShape.color === c ? "#1e40af" : "#e5e7eb" }}
                  />
                ))}
              </div>
              <label className="flex items-center gap-2 mt-3 px-2 py-2 rounded-lg border border-slate-200 bg-white cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!selectedShape.hidden}
                  onChange={(e) => handleUpdateShape(selectedShape.id, { hidden: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-xs text-slate-700">{t("admin.mapDrawing.hideInUserView")}</span>
              </label>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => setSelectedShapeId(null)}
                  className="flex-1 py-2 rounded-lg border border-slate-300 text-sm font-bold text-slate-700 hover:bg-slate-100"
                >
                  {t("admin.mapDrawing.deselect")}
                </button>
                <button
                  onClick={() => handleRemoveShape(selectedShape.id)}
                  className="flex-1 py-2 rounded-lg bg-red-500 text-sm font-bold text-white hover:bg-red-600"
                >
                  {t("common.delete")}
                </button>
              </div>
            </div>
          )}

          {/* layers list */}
          <div className="flex-1 min-h-0 overflow-y-auto p-3">
            {shapes.length === 0 && (
              <div className="text-center text-xs text-slate-400 py-8">
                {t("admin.mapDrawing.noShapesPrompt")}
              </div>
            )}
            <div className="flex flex-col gap-2">
              {shapes.map((shape, index) => (
                <div key={shape.id} className="flex items-center gap-2">

                  {/* ชื่อ layer */}
                  <div
                    onClick={() => setSelectedShapeId(shape.id)}
                    className={`flex-1 flex items-center gap-3 rounded-xl px-3 py-3 cursor-pointer border transition-colors ${selectedShapeId === shape.id
                      ? "border-blue-400 bg-slate-100"
                      : "border-gray-200 bg-white hover:bg-slate-50"
                      }`}
                  >
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <rect
                        x="1.5" y="1.5" width="15" height="15" rx="2"
                        stroke={shape.color || "#94a3b8"}
                        strokeWidth="1.5"
                        strokeDasharray="3 2"
                        fill={shape.color || "#94a3b8"}
                        fillOpacity="0.2"
                      />
                    </svg>
                    <span className="flex-1 text-sm text-slate-700 truncate">
                      {getShapeLabel(shape, index)}
                    </span>
                  </div>

                  {/* delete */}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRemoveShape(shape.id); }}
                    className="w-11 h-11 flex items-center justify-center rounded-xl border border-gray-200 bg-white text-slate-400 hover:bg-red-50 hover:text-red-500 hover:border-red-200 transition-colors"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                      <path d="M10 11v6M14 11v6" />
                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                    </svg>
                  </button>

                </div>
              ))}
            </div>
          </div>

          {/* footer buttons */}
          <div className="shrink-0 border-t border-gray-200 p-3">
            <div className="flex gap-2">
              <button className="flex-1 py-2.5 bg-white text-slate-700 rounded-lg border border-gray-200 text-sm font-semibold hover:bg-slate-50">
                {t("common.cancel")}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    <span>{t("common.saving") || "กำลังบันทึก..."}</span>
                  </>
                ) : (
                  t("common.save")
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* pending shape popup — ตั้งชื่อ + เลือกสี หลังวาดเสร็จ */}
      {pendingShape && (
        <div
          className="fixed z-[9999] inset-0"
          style={{ background: "transparent", pointerEvents: "none" }}
        >
          {/* backdrop */}
          <div
            className="absolute inset-0"
            style={{ pointerEvents: "auto" }}
            onClick={handleCancelPending}
          />
          {/* card */}
          <div
            className="absolute bg-white rounded-2xl overflow-hidden"
            style={{
              top: "90px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "340px",
              boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
              pointerEvents: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-1">
              <span className="text-[15px] font-bold text-blue-700">ตั้งค่าการวาดแผนที่</span>
              <button
                onClick={handleCancelPending}
                className="text-slate-400 hover:text-slate-600 w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-5 pb-5">
              {/* shape type */}
              <p className="text-xs text-slate-400 mb-2 mt-1">
                {drawOptions.find(o => o.id === pendingShape.type)?.name || pendingShape.type}
              </p>

              {/* name input */}
              <input
                type="text"
                autoFocus
                value={pendingName}
                onChange={(e) => setPendingName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleConfirmPending(); }}
                placeholder={`Rectangle ${shapes.length + 1}`}
                className="w-full px-4 py-2.5 text-sm rounded-xl border border-slate-200 focus:border-blue-400 focus:outline-none bg-slate-50"
              />

              {/* color swatches */}
              <div className="flex gap-2 mt-4">
                {colorOptions.map((c) => {
                  const active = pendingColor === c;
                  return (
                    <button
                      key={c}
                      onClick={() => setPendingColor(c)}
                      className="rounded-lg transition-all"
                      style={{
                        width: "40px",
                        height: "40px",
                        background: c,
                        border: active ? "2.5px solid #1e40af" : "1.5px solid #d1d5db",
                        boxShadow: active ? "0 0 0 3px #1e40af22" : "none",
                        transform: active ? "scale(1.08)" : "scale(1)",
                      }}
                    />
                  );
                })}
              </div>

              {/* confirm button */}
              <button
                onClick={handleConfirmPending}
                className="mt-5 w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm transition-colors"
              >
                บันทึกการสร้าง
              </button>
            </div>
          </div>
        </div>
      )}

      {/* save success modal */}
      {openModel && (
        <div className="fixed z-[9999] inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-10 w-[480px] max-w-full shadow-2xl text-center">
            {/* Success icon */}
            <div className="w-24 h-24 mx-auto rounded-full bg-green-50 flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center text-white">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="3.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-slate-800 mt-6 px-4">
              ระบบบันทึกข้อมูลการวาดอาคารของคุณแล้ว
            </h2>

            {/* Description */}
            <p className="text-[15px] text-slate-500 mt-4 leading-relaxed px-6 font-medium">
              ฟีเจอร์นี้ช่วยให้คุณสามารถวาดแผนที่ด้วยตนเองได้อย่างสมบูรณ์เพื่อนำไปใช้ในการแสดงผลและระบบนำทางภายในอาคาร
            </p>

            {/* Continue Button */}
            <button
              onClick={() => setOpenModel(false)}
              className="mt-8 w-full py-4 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-2xl font-semibold text-lg transition-colors border border-slate-200"
            >
              {t("admin.mapDrawing.continueBtn") || "ทำต่อ"}
            </button>

            {/* Back to buildings list link */}
            <button
              onClick={() => navigate("/admin/buildings")}
              className="mt-6 flex items-center justify-center gap-1 mx-auto text-emerald-600 hover:text-emerald-700 font-semibold text-base transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              กลับ ไปยัง หน้าสร้างอาคาร
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
